import { ChartCard, ChartValueTable } from '@/components/molecules/org-admin/charts/ChartCard';
import { ConcentrationChart } from '@/components/molecules/org-admin/charts/OverviewCharts';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import { OrgAdminErrorState } from '@/components/molecules/org-admin/OrgAdminStates';
import type { SpendDecile } from '@/services/org-admin/analysis';
import type {
  OrgServiceUsageReportUser,
  OrgAutomationUsage,
  OrgUsageReportMember,
  UsageWindowMonth,
} from '@/services/org-admin';
import { MemberUsageTable } from '../MemberUsageTable';
import { AutomationUsageRow } from '../AutomationUsageRow';

/**
 * "Who is spending it?" — the canonical per-member surface, and the distribution that summarises it.
 *
 * THIS TABLE REPLACES THREE. Per-member spend appeared on Overview ("Top members by spend"), on
 * Usage ("Usage by member") and on Analysis ("Who is spending it") — three titles, three date
 * semantics, one question. Analysis's version was a ranked bar chart of the top eight; this one is
 * the full roster with an in-cell magnitude bar, so it does the chart's job and also answers "what
 * about the person who is not in the top eight". Overview keeps a three-row preview that links here.
 *
 * The pieces that only existed here and had to survive the merge:
 *
 *   - the expandable **member × feature** drill-down, the finest-grained join in the panel;
 *   - the **automation row**, separated and explained as work with no signed-in member and no seat
 *     budget. It was measured at 87% of all spend in dev, so it is not a footnote;
 *   - the em-dash discipline in `usageScope.ts`, which returns `null` rather than `0` for a figure
 *     the API did not report — a reported zero and an absent value must never render identically.
 *
 * The **cycle/range scope toggle** is gone, from `dev`: now that the range is a calendar month, the
 * window and the cycle are the same span for every real member, so the toggle offered a choice
 * between two identical figures. Every figure here is month-scoped except the budget column, which
 * is cycle-scoped by definition — the table's caption states that mixed clock explicitly rather
 * than leaving the reader to assume one.
 *
 * Concentration moved here from Overview. It is a summary OF this table, and on a screen that did
 * not list the people it distributes over the reader had to navigate to check it.
 */

export interface ByMemberSectionProps {
  members: OrgUsageReportMember[];
  automation: OrgAutomationUsage | null | undefined;
  serviceByUsers: OrgServiceUsageReportUser[];
  concentration: SpendDecile[];
  /** The selected calendar month — names the table's period copy. */
  selection: UsageWindowMonth;
  rangeLabel: string;
  width: number | null;
  isLoading: boolean;
  isServiceByUsersLoading: boolean;
  /** Rendered in place of the drill-down's contents when its own request failed. */
  serviceByUsersError: unknown;
  isFetching: boolean;
  onEditBudget: (member: OrgUsageReportMember) => void;
}

export function ByMemberSection({
  members,
  automation,
  serviceByUsers,
  concentration,
  selection,
  rangeLabel,
  width,
  isLoading,
  isServiceByUsersLoading,
  serviceByUsersError,
  isFetching,
  onEditBudget,
}: ByMemberSectionProps) {
  return (
    <Section title="Who is spending it?">
      <MemberUsageTable
        data={members}
        selection={selection}
        isLoading={isLoading}
        onEditBudget={onEditBudget}
        serviceByUsers={serviceByUsers}
        isServiceByUsersLoading={isServiceByUsersLoading}
      />

      {/* Directly under the members table, because it is the rest of the same answer: what the org
          spent. Separate from it, because it belongs to no member and has no budget. */}
      {automation && (
        <div className="mt-3">
          <AutomationUsageRow automation={automation} scopeLabel={rangeLabel} />
        </div>
      )}

      {/* The drill-down only surfaces inside an expanded row, so its failure is reported here rather
          than silently leaving every row's drawer empty. */}
      {!!serviceByUsersError && (
        <div className="mt-3">
          <section className="oa-panel">
            <OrgAdminErrorState
              title="Couldn't load the per-member feature breakdown"
              error={serviceByUsersError}
              className="min-h-[160px]"
            />
          </section>
        </div>
      )}

      <div className="mt-3">
        <ChartCard
          testId="concentration"
          title="Spend concentration"
          description="Share of spend by decile of member, highest first — answers whether the bill is one person."
          isLoading={isLoading}
          isFetching={isFetching}
          isEmpty={!isLoading && !concentration.length}
          emptyTitle="No spend to distribute"
          emptyDescription={`Nobody spent anything ${rangeLabel}.`}
          table={
            <ChartValueTable
              caption="Share of spend by decile of member"
              columns={[
                { label: 'Decile' },
                { label: 'Members', align: 'right' },
                { label: 'Share', align: 'right' },
              ]}
              rows={concentration.map((bucket) => ({
                key: String(bucket.decile),
                cells: [
                  bucket.decile === 0
                    ? 'Top 10%'
                    : `${bucket.decile * 10}–${bucket.decile * 10 + 10}%`,
                  bucket.members.toLocaleString(),
                  `${Math.round(bucket.shareOfTotal * 100)}%`,
                ],
              }))}
            />
          }
        >
          <ConcentrationChart deciles={concentration} containerWidth={width} />
        </ChartCard>
      </div>
    </Section>
  );
}
